import axios from "axios";
import {
    SMART_PREFILL_CONFIG,
    SMART_PREFILL_ERROR_CODES,
    buildSmartPrefillPrompt,
    DEFAULT_EVENT_TYPE_FORM,
    HEX_COLOR_REGEX,
    DEFAULT_GEMINI_API_KEY
} from "../../Pages/DynamicEventTypes/constants";

const createError = (code, details = {}) => {
    const error = new Error();
    error.code = code;
    error.isSmartPrefillError = true;
    Object.assign(error, details);
    return error;
};

const getStoredApiKey = () => {
    try {
        return localStorage.getItem(SMART_PREFILL_CONFIG.apiKeyStorageKey) || "";
    } catch (storageError) {
        return "";
    }
};

const getApiKey = (overrideKey) => {
    if (typeof overrideKey === "string" && overrideKey.trim()) {
        return overrideKey.trim();
    }
    return (
        getStoredApiKey() ||
        process.env.REACT_APP_GEMINI_API_KEY ||
        process.env.GEMINI_API_KEY ||
        DEFAULT_GEMINI_API_KEY
    );
};

const normalizeString = (value, fallback) => {
    if (typeof value !== "string") {
        return fallback;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallback;
};

const normalizeInteger = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeBoolean = (value, fallback) => {
    return typeof value === "boolean" ? value : fallback;
};

const clampNumber = (value, min, max, fallback) => {
    if (!Number.isFinite(value)) {
        return fallback;
    }
    if (min > max) {
        return fallback;
    }
    return Math.min(Math.max(value, min), max);
};

const isHexColor = (value) => {
    return typeof value === "string" && HEX_COLOR_REGEX.test(value.trim());
};

const normalizeChoices = (choices) => {
    let rawChoices = [];
    if (Array.isArray(choices)) {
        rawChoices = choices;
    } else if (typeof choices === "string") {
        rawChoices = choices.split(",");
    }

    const normalized = rawChoices
        .map((choice) => (typeof choice === "string" ? choice.trim() : ""))
        .filter((choice) => choice.length > 0);

    const unique = Array.from(new Set(normalized));
    return unique.slice(0, SMART_PREFILL_CONFIG.maxChoicesPerQuestion);
};

const normalizeQuestions = (questions) => {
    if (!Array.isArray(questions)) {
        return [];
    }

    const normalized = [];
    for (let index = 0; index < questions.length; index += 1) {
        if (normalized.length >= SMART_PREFILL_CONFIG.maxQuestions) {
            break;
        }
        const rawQuestion = questions[index] || {};
        const questionText = normalizeString(rawQuestion.question, "");
        const choices = normalizeChoices(rawQuestion.choices);
        if (!questionText || choices.length < SMART_PREFILL_CONFIG.minChoicesPerQuestion) {
            continue;
        }
        normalized.push({
            question: questionText,
            choices,
            sort_order: normalizeInteger(rawQuestion.sort_order, normalized.length)
        });
    }
    return normalized;
};

const normalizeEventType = (rawEventType) => {
    const base = DEFAULT_EVENT_TYPE_FORM;
    const displayName = normalizeString(rawEventType.display_name, base.display_name);
    const emoji = normalizeString(rawEventType.emoji, base.emoji);
    const variantColor = isHexColor(rawEventType.variant_color)
        ? rawEventType.variant_color.trim()
        : base.variant_color;

    const minGuestsRaw = normalizeInteger(rawEventType.min_estimated_guests, base.min_estimated_guests);
    const maxGuestsRaw = normalizeInteger(rawEventType.max_estimated_guests, base.max_estimated_guests);
    const minGuests = clampNumber(
        minGuestsRaw,
        SMART_PREFILL_CONFIG.minGuestLimit,
        SMART_PREFILL_CONFIG.maxGuestLimit,
        base.min_estimated_guests
    );
    const maxGuests = clampNumber(
        maxGuestsRaw,
        minGuests,
        SMART_PREFILL_CONFIG.maxGuestLimit,
        base.max_estimated_guests
    );
    const defaultGuestsRaw = normalizeInteger(rawEventType.default_estimated_guests, base.default_estimated_guests);
    const defaultGuests = clampNumber(defaultGuestsRaw, minGuests, maxGuests, base.default_estimated_guests);

    const variantStrings = typeof rawEventType.variant_strings === "object" && rawEventType.variant_strings !== null
        ? rawEventType.variant_strings
        : base.variant_strings;

    return {
        display_name: displayName,
        emoji,
        variant_color: variantColor,
        min_estimated_guests: minGuests,
        max_estimated_guests: maxGuests,
        default_estimated_guests: defaultGuests,
        show_participant_names: normalizeBoolean(rawEventType.show_participant_names, base.show_participant_names),
        swappable_participant_names: normalizeBoolean(rawEventType.swappable_participant_names, base.swappable_participant_names),
        sort_order: normalizeInteger(rawEventType.sort_order, base.sort_order),
        variant_strings: variantStrings
    };
};

const extractResponseText = (response) => {
    const candidate = response?.data?.candidates?.[0];
    if (!candidate) {
        return "";
    }
    const contentText = candidate?.content?.parts?.[0]?.text;
    if (contentText) {
        return contentText;
    }
    if (typeof candidate?.output === "string") {
        return candidate.output;
    }
    if (typeof candidate?.content === "string") {
        return candidate.content;
    }
    return "";
};

const extractJsonString = (text) => {
    if (!text) {
        return null;
    }
    const trimmed = text.trim();
    if (!trimmed) {
        return null;
    }
    const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const candidate = fencedMatch ? fencedMatch[1].trim() : trimmed;
    const firstBrace = candidate.indexOf("{");
    const lastBrace = candidate.lastIndexOf("}");
    if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
        return null;
    }
    return candidate.slice(firstBrace, lastBrace + 1);
};

const parseSmartPrefillPayload = (text) => {
    const jsonString = extractJsonString(text);
    if (!jsonString) {
        return null;
    }
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        return null;
    }
};

const normalizeSmartPrefillPayload = (payload) => {
    const rawEventType = payload?.eventType ? payload.eventType : payload;
    const rawQuestions = payload?.questions || [];
    const eventType = normalizeEventType(rawEventType || {});
    const questions = normalizeQuestions(rawQuestions);
    return { eventType, questions };
};

const MODEL_PREFIX = "models/";
const DEFAULT_METHOD = SMART_PREFILL_CONFIG.methodPriority?.[0] || "generateContent";
const modelCache = {
    apiKey: "",
    models: [],
    fetchedAt: 0
};

const buildGenerateUrl = (apiKey, apiVersion, model, method) => {
    return `${SMART_PREFILL_CONFIG.apiBaseUrl}/${apiVersion}/models/${model}:${method}?key=${apiKey}`;
};

const buildModelsUrl = (apiKey, apiVersion) => {
    return `${SMART_PREFILL_CONFIG.apiBaseUrl}/${apiVersion}/models?key=${apiKey}`;
};

const getApiVersions = () => {
    return Array.isArray(SMART_PREFILL_CONFIG.apiVersions) && SMART_PREFILL_CONFIG.apiVersions.length > 0
        ? SMART_PREFILL_CONFIG.apiVersions
        : ["v1beta"];
};

const getModels = () => {
    const models = SMART_PREFILL_CONFIG.models;
    let names = [];
    if (Array.isArray(models) && models.length > 0) {
        names = models;
    } else if (SMART_PREFILL_CONFIG.model) {
        names = [SMART_PREFILL_CONFIG.model];
    } else {
        names = ["gemini-1.5-flash"];
    }
    return names.map((name) => ({ name, method: DEFAULT_METHOD }));
};

const normalizeModelName = (name) => {
    if (typeof name !== "string") {
        return "";
    }
    return name.startsWith(MODEL_PREFIX) ? name.slice(MODEL_PREFIX.length) : name;
};

const pickGenerationMethod = (methods) => {
    const priority = SMART_PREFILL_CONFIG.methodPriority || [DEFAULT_METHOD];
    if (Array.isArray(methods) && methods.length > 0) {
        for (let index = 0; index < priority.length; index += 1) {
            if (methods.includes(priority[index])) {
                return priority[index];
            }
        }
        return "";
    }
    return DEFAULT_METHOD;
};

const shouldUseCachedModels = (apiKey) => {
    const now = Date.now();
    return (
        modelCache.apiKey === apiKey &&
        modelCache.models.length > 0 &&
        now - modelCache.fetchedAt < SMART_PREFILL_CONFIG.modelCacheTtlMs
    );
};

const getCachedModelsForKey = (apiKey) => {
    return shouldUseCachedModels(apiKey) ? modelCache.models : [];
};

const setCachedModels = (apiKey, models) => {
    modelCache.apiKey = apiKey;
    modelCache.models = models;
    modelCache.fetchedAt = Date.now();
};

const extractModelsFromResponse = (response) => {
    const rawModels = response?.data?.models;
    if (!Array.isArray(rawModels)) {
        return [];
    }
    return rawModels
        .map((model) => {
            if (!model) {
                return null;
            }
            const name = normalizeModelName(model.name);
            const method = pickGenerationMethod(model.supportedGenerationMethods);
            if (!name || !method) {
                return null;
            }
            return { name, method };
        })
        .filter((model) => model);
};

const normalizeModelTargets = (modelTargets) => {
    if (!Array.isArray(modelTargets)) {
        return [];
    }
    return modelTargets
        .map((target) => {
            if (!target?.name || !target?.method) {
                return null;
            }
            return { name: target.name, method: target.method };
        })
        .filter((target) => target);
};

const getPreferredModels = (availableModels) => {
    const preferred = getModels();
    if (!Array.isArray(availableModels) || availableModels.length === 0) {
        return preferred;
    }
    const availableByName = new Map(
        availableModels.map((model) => [model.name.toLowerCase(), model])
    );
    const ordered = preferred
        .map((model) => availableByName.get(model.name.toLowerCase()))
        .filter((model) => model);
    if (ordered.length > 0) {
        return ordered;
    }
    const fallback = availableModels.filter((model) =>
        model.name.toLowerCase().includes(SMART_PREFILL_CONFIG.preferredModelSubstring)
    );
    return fallback.length > 0 ? fallback : availableModels;
};

const fetchAvailableModels = async (apiKey) => {
    if (shouldUseCachedModels(apiKey)) {
        return modelCache.models;
    }
    const apiVersions = getApiVersions();
    for (let versionIndex = 0; versionIndex < apiVersions.length; versionIndex += 1) {
        const apiVersion = apiVersions[versionIndex];
        const url = buildModelsUrl(apiKey, apiVersion);
        try {
            const response = await axios.get(url);
            const models = normalizeModelTargets(extractModelsFromResponse(response));
            if (models.length > 0) {
                setCachedModels(apiKey, models);
                return models;
            }
        } catch (error) {
            continue;
        }
    }
    return [];
};

const shouldRetryModel = (error) => {
    return error?.response?.status === 404;
};

const buildRequestBodyForMethod = (method, promptText) => {
    if (method === "generateText") {
        return {
            prompt: { text: promptText },
            temperature: SMART_PREFILL_CONFIG.temperature
        };
    }
    if (method === "generateMessage") {
        return {
            prompt: { messages: [{ content: promptText }] },
            temperature: SMART_PREFILL_CONFIG.temperature
        };
    }
    return {
        contents: [
            {
                role: SMART_PREFILL_CONFIG.requestRole,
                parts: [{ text: promptText }]
            }
        ],
        generationConfig: {
            temperature: SMART_PREFILL_CONFIG.temperature
        }
    };
};

const serializeModelTargets = (targets) => {
    if (!Array.isArray(targets)) {
        return "";
    }
    return targets
        .map((target) => `${target.name}:${target.method}`)
        .join(",");
};

export const generateSmartPrefill = async (prompt, apiKeyOverride) => {
    const trimmedPrompt = typeof prompt === "string" ? prompt.trim() : "";
    if (!trimmedPrompt) {
        throw createError(SMART_PREFILL_ERROR_CODES.emptyPrompt);
    }

    const apiKey = getApiKey(apiKeyOverride);
    if (!apiKey) {
        throw createError(SMART_PREFILL_ERROR_CODES.missingApiKey);
    }

    const safePrompt = trimmedPrompt.slice(0, SMART_PREFILL_CONFIG.maxPromptLength);
    const promptText = buildSmartPrefillPrompt(safePrompt);

    const apiVersions = getApiVersions();
    const cachedModels = getCachedModelsForKey(apiKey);
    let models = cachedModels.length > 0 ? getPreferredModels(cachedModels) : getModels();
    if (cachedModels.length === 0) {
        const availableModels = await fetchAvailableModels(apiKey);
        if (availableModels.length > 0) {
            models = getPreferredModels(availableModels);
        }
    }
    let sawNotFound = false;

    for (let versionIndex = 0; versionIndex < apiVersions.length; versionIndex += 1) {
        const apiVersion = apiVersions[versionIndex];
        for (let modelIndex = 0; modelIndex < models.length; modelIndex += 1) {
            const modelTarget = models[modelIndex];
            const requestBody = buildRequestBodyForMethod(modelTarget.method, promptText);
            const url = buildGenerateUrl(apiKey, apiVersion, modelTarget.name, modelTarget.method);
            try {
                const response = await axios.post(url, requestBody);
                const text = extractResponseText(response);
                const parsedPayload = parseSmartPrefillPayload(text);
                if (!parsedPayload) {
                    throw createError(SMART_PREFILL_ERROR_CODES.invalidResponse);
                }
                const normalized = normalizeSmartPrefillPayload(parsedPayload);
                if (!normalized.eventType.display_name || !normalized.eventType.variant_color) {
                    throw createError(SMART_PREFILL_ERROR_CODES.invalidResponse);
                }
                return normalized;
            } catch (error) {
                if (error?.isSmartPrefillError) {
                    throw error;
                }
                if (shouldRetryModel(error)) {
                    sawNotFound = true;
                    continue;
                }
                throw createError(SMART_PREFILL_ERROR_CODES.generationFailed);
            }
        }
    }

    if (sawNotFound) {
        const availableModels = await fetchAvailableModels(apiKey);
        const preferredModels = getPreferredModels(availableModels);
        if (preferredModels.length > 0 && serializeModelTargets(preferredModels) !== serializeModelTargets(models)) {
            models = preferredModels;
            for (let versionIndex = 0; versionIndex < apiVersions.length; versionIndex += 1) {
                const apiVersion = apiVersions[versionIndex];
                for (let modelIndex = 0; modelIndex < models.length; modelIndex += 1) {
                        const modelTarget = models[modelIndex];
                        const requestBody = buildRequestBodyForMethod(modelTarget.method, promptText);
                        const url = buildGenerateUrl(apiKey, apiVersion, modelTarget.name, modelTarget.method);
                    try {
                        const response = await axios.post(url, requestBody);
                        const text = extractResponseText(response);
                        const parsedPayload = parseSmartPrefillPayload(text);
                        if (!parsedPayload) {
                            throw createError(SMART_PREFILL_ERROR_CODES.invalidResponse);
                        }
                        const normalized = normalizeSmartPrefillPayload(parsedPayload);
                        if (!normalized.eventType.display_name || !normalized.eventType.variant_color) {
                            throw createError(SMART_PREFILL_ERROR_CODES.invalidResponse);
                        }
                        return normalized;
                    } catch (error) {
                        if (error?.isSmartPrefillError) {
                            throw error;
                        }
                        if (shouldRetryModel(error)) {
                            continue;
                        }
                        throw createError(SMART_PREFILL_ERROR_CODES.generationFailed);
                    }
                }
            }
        }
        throw createError(SMART_PREFILL_ERROR_CODES.modelNotFound);
    }
    throw createError(SMART_PREFILL_ERROR_CODES.generationFailed);
};

export const testGeminiKey = async (apiKeyOverride) => {
    const apiKey = getApiKey(apiKeyOverride);
    if (!apiKey) {
        throw createError(SMART_PREFILL_ERROR_CODES.missingApiKey);
    }

    const apiVersions = getApiVersions();
    let lastStatus = null;

    for (let versionIndex = 0; versionIndex < apiVersions.length; versionIndex += 1) {
        const apiVersion = apiVersions[versionIndex];
        const url = buildModelsUrl(apiKey, apiVersion);
        try {
            const response = await axios.get(url);
            const models = normalizeModelTargets(extractModelsFromResponse(response));
            if (models.length > 0) {
                setCachedModels(apiKey, models);
                return {
                    ok: true,
                    apiVersion,
                    models: models.map((model) => model.name)
                };
            }
        } catch (error) {
            lastStatus = error?.response?.status || lastStatus;
            continue;
        }
    }

    throw createError(SMART_PREFILL_ERROR_CODES.modelListFailed, { status: lastStatus });
};

const buildDataIntelligenceSqlPrompt = (schemaText, question) =>
    `You are a PostgreSQL expert. Given the following database schema (tables and columns), write a single PostgreSQL SELECT query that answers this question. Output only the SQL, no other text. No INSERT, UPDATE, or DELETE.

Schema:
${schemaText}

Question: ${question}

SQL:`;

const extractSqlFromText = (text) => {
    if (!text || typeof text !== "string") return "";
    const trimmed = text.trim();
    const codeBlock = /```(?:sql)?\s*([\s\S]*?)```/.exec(trimmed);
    return (codeBlock ? codeBlock[1] : trimmed).trim();
};

const buildDataIntelligenceExplanationPrompt = (query, resultSummary) =>
    `Explain this database query result in 1-2 short sentences for a product manager. Query: ${query}. Result summary: ${resultSummary}.`;

export const generateDataIntelligenceSql = async (schemaText, question, apiKeyOverride) => {
    const apiKey = getApiKey(apiKeyOverride);
    if (!apiKey) {
        throw createError(SMART_PREFILL_ERROR_CODES.missingApiKey);
    }
    const prompt = buildDataIntelligenceSqlPrompt(
        typeof schemaText === "string" ? schemaText : "",
        typeof question === "string" ? question.trim() : ""
    );
    const apiVersions = getApiVersions();
    let models = getCachedModelsForKey(apiKey);
    if (models.length === 0) {
        models = await fetchAvailableModels(apiKey);
    }
    if (models.length === 0) {
        models = getModels();
    }
    const preferred = getPreferredModels(models);
    const toTry = preferred.length > 0 ? preferred : models;
    for (let vi = 0; vi < apiVersions.length; vi++) {
        for (let mi = 0; mi < toTry.length; mi++) {
            const modelTarget = toTry[mi];
            const url = buildGenerateUrl(apiKey, apiVersions[vi], modelTarget.name, modelTarget.method);
            const body = buildRequestBodyForMethod(modelTarget.method, prompt);
            try {
                const response = await axios.post(url, body);
                const text = extractResponseText(response);
                const sql = extractSqlFromText(text);
                if (sql) return sql;
            } catch (err) {
                if (err?.isSmartPrefillError) throw err;
            }
        }
    }
    throw createError(SMART_PREFILL_ERROR_CODES.generationFailed);
};

export const generateDataIntelligenceExplanation = async (query, resultSummary, apiKeyOverride) => {
    const apiKey = getApiKey(apiKeyOverride);
    if (!apiKey) {
        throw createError(SMART_PREFILL_ERROR_CODES.missingApiKey);
    }
    const prompt = buildDataIntelligenceExplanationPrompt(
        typeof query === "string" ? query : "",
        typeof resultSummary === "string" ? resultSummary : ""
    );
    const apiVersions = getApiVersions();
    let models = getCachedModelsForKey(apiKey);
    if (models.length === 0) {
        models = await fetchAvailableModels(apiKey);
    }
    if (models.length === 0) {
        models = getModels();
    }
    const preferred = getPreferredModels(models);
    const toTry = preferred.length > 0 ? preferred : models;
    for (let vi = 0; vi < apiVersions.length; vi++) {
        for (let mi = 0; mi < toTry.length; mi++) {
            const modelTarget = toTry[mi];
            const url = buildGenerateUrl(apiKey, apiVersions[vi], modelTarget.name, modelTarget.method);
            const body = buildRequestBodyForMethod(modelTarget.method, prompt);
            try {
                const response = await axios.post(url, body);
                const text = extractResponseText(response);
                if (text && text.trim()) return text.trim();
            } catch (err) {
                if (err?.isSmartPrefillError) throw err;
            }
        }
    }
    return "";
};
